import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { parseMutationError } from '../api/errors';
import { useDeleteProduct, useProduct } from '../api/products';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function ProductDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const productQuery = useProduct(id);
  const remove = useDeleteProduct(id);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

  const product = productQuery.data;

  if (productQuery.isPending) return <Text p="md">Loading…</Text>;
  if (productQuery.isError || !product) return <Text p="md">Could not load product.</Text>;

  const retired = !!product.deletedAt;

  function handleDelete() {
    remove.mutate(undefined, {
      onSuccess: () => {
        notifications.show({ color: 'green', message: 'Product deleted.' });
        navigate('/products');
      },
      onError: (err) => {
        setError(parseMutationError(err, 'Could not delete product.'));
        closeConfirm();
      },
    });
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Group>
          <Title order={2}>{product.code}</Title>
          {retired && (
            <Badge color="gray" variant="light">
              retired
            </Badge>
          )}
        </Group>
        <Group>
          <Button variant="subtle" component={Link} to="/products">
            Back to products
          </Button>
          {!retired && (
            <Button variant="default" component={Link} to={`/products/${product.id}/consent`}>
              Default consents
            </Button>
          )}
          {!retired && (
            <Button component={Link} to={`/products/${product.id}/edit`}>
              Edit
            </Button>
          )}
        </Group>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Card withBorder padding="lg" maw={680}>
        <Group gap="lg">
          <div>
            <Text size="sm" c="dimmed">
              Display name
            </Text>
            <Text fw={500}>{product.displayName}</Text>
          </div>
          <div>
            <Text size="sm" c="dimmed">
              Status
            </Text>
            <Badge color={product.status === 'active' ? 'green' : 'gray'} variant="light">
              {product.status}
            </Badge>
          </div>
          <div>
            <Text size="sm" c="dimmed">
              Assignments
            </Text>
            <Badge color={product.assignment_count > 0 ? 'blue' : 'gray'} variant="light">
              {product.assignment_count}
            </Badge>
          </div>
          <div>
            <Text size="sm" c="dimmed">
              User grants
            </Text>
            <Badge color={product.grant_count > 0 ? 'blue' : 'gray'} variant="light">
              {product.grant_count}
            </Badge>
          </div>
        </Group>
      </Card>

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Title order={5}>
            Assigned to {product.assignment_count} organisation
            {product.assignment_count === 1 ? '' : 's'}
          </Title>
          {product.assignments.length === 0 ? (
            <Text c="dimmed">No active assignments.</Text>
          ) : (
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Organisation</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Assigned</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {product.assignments.map((a) => (
                  <Table.Tr key={a.organisationId}>
                    <Table.Td>{a.organisationName}</Table.Td>
                    <Table.Td>
                      <Badge color={a.status === 'active' ? 'green' : 'gray'} variant="light">
                        {a.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{formatDate(a.assignedAt)}</Table.Td>
                    <Table.Td>
                      <Anchor component={Link} to={`/orgs/${a.organisationId}/products`} size="sm">
                        manage
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Card>

      {!retired && (
        <Card withBorder padding="lg" maw={680}>
          <Stack gap="sm">
            <Title order={5}>Danger zone</Title>
            <Group>
              <Button color="red" variant="subtle" onClick={openConfirm}>
                Delete product
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      <Modal
        opened={confirmOpen}
        onClose={closeConfirm}
        title="Delete product"
        transitionProps={{ duration: 0 }}
      >
        <Stack>
          {product.assignment_count > 0 ? (
            <Text>
              This product is assigned to {product.assignment_count} organisation
              {product.assignment_count === 1 ? '' : 's'}. Revoke the assignments first before
              deleting.
            </Text>
          ) : (
            <Text>Delete {product.code}? This is reversible.</Text>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeConfirm}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDelete}
              loading={remove.isPending}
              disabled={product.assignment_count > 0}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
